// The Crew / Pilot FILTER on a server-scoped register (H199, H248).
//
// Aaron, 2026-09-29 on Expenses — "move the crew picker into the filter" — and
// 2026-09-30 on Timesheets — "crew picker should be in filters". A toolbar
// `CrewPicker` beside the standard FILTERS button is two filter surfaces on
// one list. The house shape (FlightsList `pic`, FlightDutyList `uid`) is a
// `singleSelect` column whose options are the crew, driven by the FILTERS
// panel; clearing the filter is what "All Crew" was.
//
// It stays a SERVER param. Each register's route takes the crew member as a
// named arg (`subject_user_id` on /v6/flight_expenses, `uid` on
// /v6/timesheets), so the panel's item is lifted OUT of `params.filter` and
// sent as that arg — never a client filter over the page the grid holds, and
// never applied twice.
//
// Who is offered it is the caller's call, and the rule is the same everywhere
// (H178, `useSeesAllPilots`): not a scoped pilot, and not while the scope is
// loading. The options are the Pilots group (`useFdCrew` + `crewPickerOptions`,
// fdCrew.ts), never every user.
import {
  GridColDef,
  GridValidRowModel,
  getGridSingleSelectOperators,
} from "@mui/x-data-grid-pro";

/**
 * `is` ONLY. The route param takes one uid; `is not` and `is any of` have no
 * server meaning, and a panel operator that is silently ignored is the
 * inert-filter defect A5 fixed. Same restriction shape as ServiceOrderList's
 * EXACT_TEXT_FILTER_OPERATORS.
 */
export const CREW_FILTER_OPERATORS = getGridSingleSelectOperators().filter(
  (op) => op.value === "is"
);

/**
 * Split the grid's serialised filter list (components/datagrid/DataGrid.tsx
 * emits each item as a JSON string in `params.filter`) into the crew uid on
 * `field` and everything else. The crew item is REMOVED from the list either
 * way, so it is never applied twice — once as the named param, once as a spec
 * — and a pilot's restored saved item is dropped rather than sent.
 */
export function splitCrewFilter(
  filter: unknown,
  field: string
): { uid: number | null; rest: unknown[] } {
  const items = Array.isArray(filter) ? filter : filter ? [filter] : [];
  let uid: number | null = null;
  const rest: unknown[] = [];
  for (const raw of items) {
    let f: any = null;
    try {
      f = typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch {
      // Not a filter this module wrote; pass it through untouched.
    }
    if (f?.field !== field) {
      rest.push(raw);
      continue;
    }
    const n = Number(f.value);
    if (["is", "==", "equals"].includes(String(f.op)) && Number.isFinite(n) && n > 0) {
      uid = n;
    }
  }
  return { uid, rest };
}

/**
 * The Crew / Pilot column. Meant to be HIDDEN by default (the caller's
 * `initialState.columns.columnVisibilityModel`): the register's own name
 * column already names the row, and this one is there to be filtered on —
 * MUI's FILTERS panel lists hidden columns too
 * (`gridFilterableColumnDefinitionsSelector` filters ALL column defs).
 */
export function crewFilterColumn<R extends GridValidRowModel>(
  field: string,
  valueOptions: { value: number; label: string }[]
): GridColDef<R> {
  return {
    field,
    headerName: "Crew / Pilot",
    flex: 0.9,
    sortable: false,
    type: "singleSelect",
    filterable: true,
    filterOperators: CREW_FILTER_OPERATORS,
    valueOptions,
    valueGetter: (p: any) => p.row[field] ?? "",
    renderCell: (p: any) => {
      const uid = p.row[field];
      if (!uid) return "—";
      return valueOptions.find((o) => o.value === uid)?.label ?? String(uid);
    },
  } as GridColDef<R>;
}
